import { useCallback, useEffect, useRef, useState } from 'react';
import { useSharedCart } from '@/components/shared-cart/SharedCartProvider';
import { useUserStore } from '@/stores/userStore';

// Peer-to-peer voice and screen share for a party session.
//
// Topology: full mesh (fine for family-and-friends group sizes). Signaling
// rides the session WebSocket: `voice_joined`/`voice_left` are broadcast,
// `webrtc_offer`/`webrtc_answer`/`webrtc_ice` are relayed by the hub to a
// single target peer. Existing voice members initiate the first offer toward
// a newcomer; renegotiation (screen share) follows the perfect-negotiation
// pattern with politeness decided by userId ordering.

const ICE_SERVERS: RTCConfiguration = {
  iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
};

interface PeerEntry {
  pc: RTCPeerConnection;
  makingOffer: boolean;
  polite: boolean;
  audioEl: HTMLAudioElement | null;
  analyser?: { ctx: AudioContext; node: AnalyserNode };
}

export interface PartyRTCState {
  inVoice: boolean;
  muted: boolean;
  voiceMembers: string[]; // userIds currently in voice (including self when joined)
  speaking: Record<string, boolean>;
  presenterId: string | null;
  presenterStream: MediaStream | null;
  isSharingScreen: boolean;
  joinVoice: () => Promise<void>;
  leaveVoice: () => void;
  toggleMute: () => void;
  startScreenShare: () => Promise<void>;
  stopScreenShare: () => void;
}

export function usePartyRTC(): PartyRTCState {
  const { sendMessage, addMessageListener, isConnected } = useSharedCart();
  const myId = useUserStore.getState().user?.id || '';

  const [inVoice, setInVoice] = useState(false);
  const [muted, setMuted] = useState(false);
  const [voiceMembers, setVoiceMembers] = useState<string[]>([]);
  const [speaking, setSpeaking] = useState<Record<string, boolean>>({});
  const [presenterId, setPresenterId] = useState<string | null>(null);
  const [presenterStream, setPresenterStream] = useState<MediaStream | null>(null);
  const [isSharingScreen, setIsSharingScreen] = useState(false);

  const peersRef = useRef<Map<string, PeerEntry>>(new Map());
  const localStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);
  const inVoiceRef = useRef(false);
  const localAnalyserRef = useRef<{ ctx: AudioContext; node: AnalyserNode } | null>(null);

  const attachAnalyser = (stream: MediaStream) => {
    try {
      const ctx = new AudioContext();
      const source = ctx.createMediaStreamSource(stream);
      const node = ctx.createAnalyser();
      node.fftSize = 512;
      source.connect(node);
      return { ctx, node };
    } catch {
      return undefined;
    }
  };

  const isLoud = (node: AnalyserNode) => {
    const data = new Uint8Array(node.fftSize);
    node.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      const v = (data[i] - 128) / 128;
      sum += v * v;
    }
    return Math.sqrt(sum / data.length) > 0.02;
  };

  // Speaking indicator loop
  useEffect(() => {
    const timer = setInterval(() => {
      const next: Record<string, boolean> = {};
      if (localAnalyserRef.current && inVoiceRef.current && !muted) {
        next[myId] = isLoud(localAnalyserRef.current.node);
      }
      peersRef.current.forEach((entry, peerId) => {
        if (entry.analyser) next[peerId] = isLoud(entry.analyser.node);
      });
      setSpeaking(prev => {
        const changed = Object.keys({ ...prev, ...next }).some(k => !!prev[k] !== !!next[k]);
        return changed ? next : prev;
      });
    }, 250);
    return () => clearInterval(timer);
  }, [myId, muted]);

  const createPeer = useCallback(
    (peerId: string): PeerEntry => {
      const existing = peersRef.current.get(peerId);
      if (existing) return existing;

      const pc = new RTCPeerConnection(ICE_SERVERS);
      const entry: PeerEntry = {
        pc,
        makingOffer: false,
        polite: myId < peerId,
        audioEl: null,
      };
      peersRef.current.set(peerId, entry);

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(t => pc.addTrack(t, localStreamRef.current!));
      }
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(t => pc.addTrack(t, screenStreamRef.current!));
      }

      pc.onicecandidate = e => {
        if (e.candidate) {
          sendMessage('webrtc_ice', { candidate: e.candidate.toJSON(), target: peerId });
        }
      };

      pc.onnegotiationneeded = async () => {
        try {
          entry.makingOffer = true;
          await pc.setLocalDescription();
          sendMessage('webrtc_offer', { sdp: pc.localDescription, target: peerId });
        } catch (err) {
          console.error('negotiation error:', err);
        } finally {
          entry.makingOffer = false;
        }
      };

      pc.ontrack = e => {
        const stream = e.streams[0];
        if (e.track.kind === 'audio') {
          if (!entry.audioEl) {
            entry.audioEl = new Audio();
            entry.audioEl.autoplay = true;
          }
          entry.audioEl.srcObject = stream;
          entry.analyser = attachAnalyser(stream) || entry.analyser;
          setVoiceMembers(prev => (prev.includes(peerId) ? prev : [...prev, peerId]));
        } else {
          const videoStream = new MediaStream([e.track]);
          setPresenterId(peerId);
          setPresenterStream(videoStream);
          e.track.onended = () => {
            setPresenterId(prev => (prev === peerId ? null : prev));
            setPresenterStream(prev => (prev === videoStream ? null : prev));
          };
        }
      };

      pc.onconnectionstatechange = () => {
        if (['failed', 'closed', 'disconnected'].includes(pc.connectionState)) {
          // Let a fresh voice_joined re-establish the peer if they return
          if (pc.connectionState === 'failed') pc.close();
        }
      };

      return entry;
    },
    [myId, sendMessage]
  );

  const destroyPeer = useCallback((peerId: string) => {
    const entry = peersRef.current.get(peerId);
    if (!entry) return;
    entry.pc.close();
    if (entry.audioEl) entry.audioEl.srcObject = null;
    entry.analyser?.ctx.close().catch(() => {});
    peersRef.current.delete(peerId);
    setVoiceMembers(prev => prev.filter(id => id !== peerId));
    setPresenterId(prev => (prev === peerId ? null : prev));
    setPresenterStream(prev => (presenterId === peerId ? null : prev));
  }, [presenterId]);

  // Signaling
  useEffect(() => {
    const unsubscribe = addMessageListener(async message => {
      const from = message.userId || '';
      if (!from || from === myId) return;

      switch (message.type) {
        case 'voice_joined': {
          setVoiceMembers(prev => (prev.includes(from) ? prev : [...prev, from]));
          // Existing members initiate toward the newcomer
          if (inVoiceRef.current) {
            createPeer(from); // addTrack fires onnegotiationneeded → offer
          }
          break;
        }

        case 'voice_left':
        case 'user_left': {
          destroyPeer(from);
          break;
        }

        case 'webrtc_offer': {
          const entry = createPeer(from);
          const { pc } = entry;
          const offerCollision = entry.makingOffer || pc.signalingState !== 'stable';
          if (!entry.polite && offerCollision) return; // impolite peer ignores glare
          try {
            await pc.setRemoteDescription(message.data.sdp);
            await pc.setLocalDescription();
            sendMessage('webrtc_answer', { sdp: pc.localDescription, target: from });
          } catch (err) {
            console.error('offer handling error:', err);
          }
          break;
        }

        case 'webrtc_answer': {
          const entry = peersRef.current.get(from);
          if (entry) {
            try {
              await entry.pc.setRemoteDescription(message.data.sdp);
            } catch (err) {
              console.error('answer handling error:', err);
            }
          }
          break;
        }

        case 'webrtc_ice': {
          const entry = peersRef.current.get(from);
          if (entry && message.data?.candidate) {
            try {
              await entry.pc.addIceCandidate(message.data.candidate);
            } catch {
              // Benign during renegotiation
            }
          }
          break;
        }

        case 'screen_share_stopped': {
          setPresenterId(prev => (prev === from ? null : prev));
          if (presenterId === from) setPresenterStream(null);
          break;
        }
      }
    });
    return unsubscribe;
  }, [addMessageListener, createPeer, destroyPeer, myId, presenterId, sendMessage]);

  const joinVoice = useCallback(async () => {
    if (inVoiceRef.current || !isConnected) return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    localStreamRef.current = stream;
    localAnalyserRef.current = attachAnalyser(stream) || null;
    inVoiceRef.current = true;
    setInVoice(true);
    setMuted(false);
    setVoiceMembers(prev => (prev.includes(myId) ? prev : [...prev, myId]));

    // Add our audio to any peers that already exist (e.g. we were receiving)
    peersRef.current.forEach(entry => {
      stream.getTracks().forEach(t => entry.pc.addTrack(t, stream));
    });

    sendMessage('voice_joined');
  }, [isConnected, myId, sendMessage]);

  const leaveVoice = useCallback(() => {
    if (!inVoiceRef.current) return;
    inVoiceRef.current = false;
    setInVoice(false);
    sendMessage('voice_left');
    localStreamRef.current?.getTracks().forEach(t => t.stop());
    localStreamRef.current = null;
    localAnalyserRef.current?.ctx.close().catch(() => {});
    localAnalyserRef.current = null;
    peersRef.current.forEach((_, peerId) => destroyPeer(peerId));
    setVoiceMembers(prev => prev.filter(id => id !== myId));
  }, [destroyPeer, myId, sendMessage]);

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const next = !muted;
    stream.getAudioTracks().forEach(t => (t.enabled = !next));
    setMuted(next);
  }, [muted]);

  const startScreenShare = useCallback(async () => {
    if (screenStreamRef.current) return;
    const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
    screenStreamRef.current = stream;
    setIsSharingScreen(true);
    setPresenterId(myId);
    setPresenterStream(stream);

    const videoTrack = stream.getVideoTracks()[0];
    peersRef.current.forEach(entry => {
      entry.pc.addTrack(videoTrack, stream);
    });
    sendMessage('screen_share_started');

    videoTrack.onended = () => stopScreenShareInternal();
  }, [myId, sendMessage]);

  const stopScreenShareInternal = useCallback(() => {
    const stream = screenStreamRef.current;
    if (!stream) return;
    stream.getTracks().forEach(t => t.stop());
    peersRef.current.forEach(entry => {
      entry.pc.getSenders().forEach(sender => {
        if (sender.track && sender.track.kind === 'video') {
          entry.pc.removeTrack(sender);
        }
      });
    });
    screenStreamRef.current = null;
    setIsSharingScreen(false);
    setPresenterId(prev => (prev === myId ? null : prev));
    setPresenterStream(prev => (prev === stream ? null : prev));
    sendMessage('screen_share_stopped');
  }, [myId, sendMessage]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      localStreamRef.current?.getTracks().forEach(t => t.stop());
      screenStreamRef.current?.getTracks().forEach(t => t.stop());
      peersRef.current.forEach(entry => entry.pc.close());
      peersRef.current.clear();
    };
  }, []);

  return {
    inVoice,
    muted,
    voiceMembers,
    speaking,
    presenterId,
    presenterStream,
    isSharingScreen,
    joinVoice,
    leaveVoice,
    toggleMute,
    startScreenShare,
    stopScreenShare: stopScreenShareInternal,
  };
}
